import * as React from "react"
import { Building2, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { useBookingT } from "@/hooks/use-translations"
import {
  cachedRead,
  fetchLegalEntities,
  updateConnection,
  type HrisConnection,
  type HrisLegalEntities,
} from "@/features/integrations/hris/hrisApi"

type Props = {
  connection: HrisConnection
  onSaved: (connection: HrisConnection) => void
}

const SKIP = "skip"

/**
 * Which Travel Code company each legal entity of the HR system lands in, plus
 * the two choices that change who gets an account. An unmatched legal entity is
 * not synced: its employees show in the preview as needing attention.
 */
export const HrisMappingCard = React.forwardRef<HTMLDivElement, Props>(function HrisMappingCard({ connection, onSaved }, ref) {
  const nationalityOnly = connection.identityFields.length === 1 && connection.identityFields[0] === "nationality"
  const t = useBookingT()
  const [data, setData] = React.useState<HrisLegalEntities | null>(() => cachedRead<HrisLegalEntities>(`legal-entities:${connection.id}`) ?? null)
  const [loadError, setLoadError] = React.useState<string | null>(null)
  const [map, setMap] = React.useState<Record<string, number>>(connection.settings.legalEntityMap)
  const [personalEmail, setPersonalEmail] = React.useState(connection.settings.emailSource === "work_then_personal")
  const [createDepartments, setCreateDepartments] = React.useState(connection.settings.createDepartments)
  const [travellerIdentity, setTravellerIdentity] = React.useState(connection.settings.travellerIdentity ?? true)
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    fetchLegalEntities(connection.id)
      .then((res) => !cancelled && setData(res))
      .catch((e: Error) => !cancelled && setLoadError(e.message))
    return () => {
      cancelled = true
    }
  }, [connection.id])

  const save = async () => {
    setSaving(true)
    try {
      onSaved(
        await updateConnection(connection.id, {
          settings: {
            legalEntityMap: map,
            emailSource: personalEmail ? "work_then_personal" : "work",
            createDepartments,
            travellerIdentity,
          },
        }),
      )
      toast.success(t("hris_mapping_saved", "Saved. Run a preview to see the effect."))
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const setCompany = (entityId: string, value: string) =>
    setMap((prev) => {
      const next = { ...prev }
      if (value === SKIP) delete next[entityId]
      else next[entityId] = Number(value)
      return next
    })

  return (
    <Card ref={ref}>
      <CardContent className="space-y-5 p-6">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-neutral-900">{t("hris_mapping_title", "Companies")}</h2>
          <p className="max-w-2xl text-sm text-neutral-800">
            {t(
              "hris_mapping_intro",
              "Choose the Travel Code company for each legal entity. Employees of a legal entity left unmatched are not synced.",
            )}
          </p>
        </div>

        {loadError && <p className="text-sm text-error-700">{loadError}</p>}
        {!data && !loadError && (
          <div className="space-y-2">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        )}

        {data && (
          <div className="divide-y divide-neutral-200 rounded-md border border-neutral-200">
            {data.legalEntities.map((entity) => (
              <div key={entity.id} className="flex items-center justify-between gap-4 px-4 py-3 max-md:flex-col max-md:items-stretch">
                <span className="flex items-center gap-2 text-sm text-neutral-900">
                  <Building2 className="size-4 shrink-0 text-neutral-600" />
                  {entity.name}
                </span>
                <Select value={map[entity.id] ? String(map[entity.id]) : SKIP} onValueChange={(v) => setCompany(entity.id, v)}>
                  <SelectTrigger className="w-72 max-md:w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SKIP}>{t("hris_mapping_skip", "Do not sync")}</SelectItem>
                    {data.companies.map((company) => (
                      <SelectItem key={company.id} value={String(company.id)}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3">
          <label className="flex items-start justify-between gap-4">
            <span className="space-y-0.5">
              <span className="block text-sm font-medium text-neutral-900">
                {t("hris_personal_email", "Use the personal email when there is no work email")}
              </span>
              <span className="block text-sm text-neutral-800">
                {t("hris_personal_email_hint", "Otherwise employees without a work email in the HR system are not given an account.")}
              </span>
            </span>
            <Switch checked={personalEmail} onCheckedChange={setPersonalEmail} />
          </label>
          <label className="flex items-start justify-between gap-4">
            <span className="space-y-0.5">
              <span className="block text-sm font-medium text-neutral-900">
                {t("hris_create_departments", "Create the departments that are missing")}
              </span>
              <span className="block text-sm text-neutral-800">
                {t("hris_create_departments_hint", "Departments are matched by name; up to three levels are kept.")}
              </span>
            </span>
            <Switch checked={createDepartments} onCheckedChange={setCreateDepartments} />
          </label>
          <label className="flex items-start justify-between gap-4">
            <span className="space-y-0.5">
              <span className="block text-sm font-medium text-neutral-900">
                {nationalityOnly
                  ? t("hris_traveller_identity_nat", "Receive nationality")
                  : t("hris_traveller_identity", "Receive date of birth, sex and nationality")}
              </span>
              <span className="block text-sm text-neutral-800">
                {travellerIdentity
                  ? nationalityOnly
                    ? t("hris_traveller_identity_nat_on", "{name} does not share date of birth and sex: each traveller enters them before their first flight. Values a traveller entered themselves are never overwritten.").replace("{name}", connection.providerName)
                    : t("hris_traveller_identity_on", "Airlines need them to issue tickets. Values a traveller entered themselves are never overwritten.")
                  : t("hris_traveller_identity_off", "Not read from the HR system. Each traveller enters them before their first flight; until then tickets cannot be issued for them.")}
              </span>
            </span>
            <Switch checked={travellerIdentity} onCheckedChange={setTravellerIdentity} />
          </label>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving || !data}>
            {saving && <Loader2 className="size-4 animate-spin" />}
            {t("hris_save", "Save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
})
